import { OptionallyVisible } from 'components';
import { useTranslation } from 'react-i18next';

import { Panel } from 'components/containers';
import { CustodialWalletTransactionStatusBadge } from 'components/pwcRefundTransactionBadge';

import { CurrencyCode } from 'types/currency';
import { CustodialWalletTransaction } from 'types/custodialWallet';

import { formatDateLocal } from 'utils/date';
import { formatFiatCurrencyI18n } from 'utils/i18n';

import { TopAreaSection } from './components/topArea';
import { LABELS } from './keys';
import { Container, Item, ItemName, ItemValue, SectionWrapper, SubContainer, UniqueItemValue } from './topup.styles';

interface TopUpDetailsProps {
  isS4cUser?: boolean;
  transaction: CustodialWalletTransaction;
  currency?: CurrencyCode;
}

const TopUpDetails = ({ isS4cUser, transaction, currency }: TopUpDetailsProps) => {
  const { t, i18n } = useTranslation();

  const merchantCurrencyCode = currency ?? CurrencyCode.USD;

  return (
    <>
      <TopAreaSection transaction={transaction} currencyCode={merchantCurrencyCode} />
      <Container>
        <SubContainer>
          <Panel label={t(LABELS.TOPUP_SECTION_TITLE)}>
            <SectionWrapper>
              <Item>
                <ItemName>{t(LABELS.TOPUP_SECTION.ID)}</ItemName>
                <UniqueItemValue>{transaction.uuid}</UniqueItemValue>
              </Item>
              <Item>
                <ItemName>{t(LABELS.TOPUP_SECTION.AMOUNT)}</ItemName>
                <ItemValue>{formatFiatCurrencyI18n(i18n.language)(Number(transaction.amount), merchantCurrencyCode)}</ItemValue>
              </Item>
              <Item>
                <ItemName>{t(LABELS.TOPUP_SECTION.RECEIVED_AT)}</ItemName>
                <ItemValue>{formatDateLocal(transaction.createdAt)}</ItemValue>
              </Item>
              <Item>
                <ItemName>{t(LABELS.TOPUP_SECTION.STATUS)}</ItemName>
                <ItemValue>
                  <CustodialWalletTransactionStatusBadge status={transaction.status} />
                </ItemValue>
              </Item>
              <OptionallyVisible visible={isS4cUser}>
                <Item>
                  <ItemName>{t(LABELS.TOPUP_SECTION.BRIDGE_ACTIVITY_ID)}</ItemName>
                  <ItemValue>{transaction.liquidityProviderId}</ItemValue>
                </Item>
              </OptionallyVisible>
            </SectionWrapper>
          </Panel>
        </SubContainer>

        <SubContainer>
          <Panel label={t(LABELS.SOURCE_SECTION_TITLE)}>
            <SectionWrapper>
              <Item>
                <ItemName>{t(LABELS.SOURCE_SECTION.PAYMENT_RAIL)}</ItemName>
                <ItemValue>{t(LABELS.PAYMENT_RAIL[transaction.paymentRail])}</ItemValue>
              </Item>
              <Item>
                <ItemName>{t(LABELS.SOURCE_SECTION.SENDER_NAME)}</ItemName>
                <ItemValue>{transaction.senderName}</ItemValue>
              </Item>
              <Item>
                <ItemName>{t(LABELS.SOURCE_SECTION.SENDER_ROUTING_NUMBER)}</ItemName>
                <ItemValue>{transaction.senderRoutingNumber}</ItemValue>
              </Item>
              <Item>
                <ItemName>{t(LABELS.SOURCE_SECTION.TRANSFER_MEMO)}</ItemName>
                <ItemValue>{transaction.transferMemo}</ItemValue>
              </Item>
              <Item>
                <ItemName>{t(LABELS.SOURCE_SECTION.TRACE_NUMBER)}</ItemName>
                <ItemValue>{transaction.traceNumber}</ItemValue>
              </Item>
            </SectionWrapper>
          </Panel>
        </SubContainer>
      </Container>
    </>
  );
};

export default TopUpDetails;
